'use client'
import { FC } from 'react'
import Link from 'next/link'
import { generatePath } from '@utils/path.utils'
import type { PreIpoFundCardMetadata } from '@domain/landing/homePageFundPayload'
import type { StrapiComponent, StrapiFundData } from '@types'
import { FundStage, isComingSoonStage } from '@domain/funds/model'
import { createStrapiMediaUrl } from '@utils/strapi.utils'
import { Button } from '@components/ui/button'
import { SimpleStat } from '@components/shared/simple-stat/SimpleStat'
import { paths } from '@router/paths'
import { FundCardWithSections } from '../fund-card/FundCardWithSections'
import { formatNumberWithShortcuts } from '@utils'
import { get } from 'lodash'
import { RegisterInterestButton } from '@domain/pre-ipo-access/components/RegisterInterestDialog'

export interface PreIpoFundComingCardProps {
  fundMetadata: PreIpoFundCardMetadata | StrapiComponent<StrapiFundData>
  variant?: 'light' | 'dark'
  stage?: string
}

export const PreIpoFundComingCard: FC<PreIpoFundComingCardProps> = ({
  fundMetadata,
  variant = 'light',
  stage = FundStage.Upcoming,
}) => {
  const canRegisterInterest = !isComingSoonStage(stage)
  const { fundDescription, fundCoverImage, referenceValue } = fundMetadata
  const fundUrlId = get(fundMetadata, 'fundUrlId', '')
  const inAppValuation = fundMetadata.communityFundCustomData?.currentValuation
  const primaryValuationValue = inAppValuation
    ? formatNumberWithShortcuts(inAppValuation)
    : referenceValue
      ? formatNumberWithShortcuts(referenceValue)
      : 'TBA'
  const primaryValuationPrefix = inAppValuation || referenceValue ? '$' : undefined
  const detailsPath = generatePath(paths.funds.exclusiveAccess.preIPOAccessDetails, {
    fundId: fundUrlId,
  })

  return (
    <FundCardWithSections
      title={fundMetadata.fundName}
      description={fundDescription}
      stage={stage as FundStage}
      variant={variant}
      detailsPath={detailsPath}
      proofLink={fundMetadata.proofLink}
      imgIcon={createStrapiMediaUrl(fundCoverImage)}
      content={
        <div className="flex flex-col gap-3 w-full">
          <SimpleStat
            label="WLTH entry"
            labelClassName={`uppercase ${variant === 'dark' ? 'text-white' : ''}`}
            valueClassName={variant === 'dark' ? 'text-white' : ''}
            prefixClassName={variant === 'dark' ? 'text-white' : ''}
            value={primaryValuationValue}
            prefix={primaryValuationPrefix}
          />
          {!canRegisterInterest && (
            <SimpleStat
              label="Company valuation"
              labelClassName={`uppercase ${variant === 'dark' ? 'text-white' : ''}`}
              valueClassName={variant === 'dark' ? 'text-white' : ''}
              value="TBA"
              prefix="$"
              prefixClassName={variant === 'dark' ? 'text-white' : ''}
            />
          )}
        </div>
      }
      footerButtons={
        <>
          <Link
            href={detailsPath}
            className="w-full sm:w-1/2"
            aria-label={`View details for ${fundMetadata.fundName}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant={variant === 'dark' ? 'secondaryWhite' : 'secondary'}
              size="md"
              className="w-full"
            >
              More info
            </Button>
          </Link>
          {canRegisterInterest && (
            <div className="w-full sm:w-1/2">
              <RegisterInterestButton
                fundId={fundUrlId}
                fundName={fundMetadata.fundName}
                minInvestment={fundMetadata.minInvestmentValue}
                buttonClassName="!w-full"
              />
            </div>
          )}
        </>
      }
    />
  )
}
